/*
 * Quicken Simple Amount Field (QAmountField)
 */

// BASE
import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import numeral from 'numeral';
import classNames from 'classnames';

// CUSTOM COMPONENTS

// SELECTORS, ACTIONS, UTILS
import getSymbolFromCurrency from 'currency-symbol-map';

// MUI COMPONENTS
import Typography from '@material-ui/core/Typography';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';

// PATH RELATIVE IMPORTS
import { styles } from './styles';

const propTypes = {
  editable: PropTypes.bool,
  InputProps: PropTypes.object,
  onSubmit: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  value: PropTypes.number,
  currency: PropTypes.string,
  omitCents: PropTypes.bool,
  submitOnBlur: PropTypes.bool,
  hideCurrency: PropTypes.bool,
  autoFocus: PropTypes.bool,
  id: PropTypes.string,
  textFieldProps: PropTypes.object,
  styleProps: PropTypes.object,
  wrapperStyles: PropTypes.object, // styling for the wrapper div so that input can fill a parent container
};

// STYLES HOOK CREATION
const useStyles = makeStyles(styles);

const qAmountParts = (val, formatString) => {
  const sign = val ? (Math.abs(val)/val) : 1;
  // 0 / 0 yields NaN so added check here to assign no sign
  const signChar = sign > 0 || Number.isNaN(sign) ? '' : '-';
  return { sign: signChar, numberString: numeral(Math.abs(val)).format(formatString) };
};

const QAmountField = (props) => {

  const renderStaticValue = (val) => {
    const formatString = omitCents ? '0,00' : '0,00.00';
    const parts = qAmountParts(val, formatString, currencySymbol);
    return `${parts.sign}${currencySymbol} ${parts.numberString}`;
  };
  const renderEditableValue = (val, withComma = false) => {
    const formatString = `${withComma ? '0,0' : '0'}${omitCents ? '' : '.00'}`;
    const parts = qAmountParts(val, formatString, currencySymbol);
    return `${parts.sign}${parts.numberString}`;
  };

  // PROPS ============================================================================
  const { editable, InputProps, value, currency, hideCurrency, omitCents, onSubmit, onBlur,
    submitOnBlur, autoFocus, id, textFieldProps, styleProps, onChange, wrapperStyles} = props;

  // STATE ============================================================================
  const [fieldValue, setFieldValue] = useState('0');
  const [hasFocus, setHasFocus] = useState(false);

  // SELECTORS ==========================================================================
  const currencySymbol = hideCurrency ? '' : getSymbolFromCurrency(currency || 'USD');

  // EFFECTS ============================================================================
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const newValue = editable ? renderEditableValue(value, !hasFocus) : renderStaticValue(value);
    setFieldValue(newValue);
  }, [value, hasFocus]);

  // STYLES ============================================================================
  const classes = useStyles(props);

  // INTERNAL FUNCTIONS
  const handleKeyDown = (e) => {
    const legalChars = omitCents ? '0123456789-,' : '0123456789-,';
    if (e.key === 'Enter') {
      if (onSubmit) {
        e.preventDefault();
        onSubmit({ target: { value: validateField(fieldValue)}});
      }
    }
    // only allow one period
    if (e.key === '.') {
      // never allow if omitCents
      if (omitCents) e.preventDefault();
      // if period already exists, allow if it is in the selection
      if (fieldValue.substr(e.target.selectionStart, e.target.selectionEnd).indexOf('.') === -1) {
        if (fieldValue.indexOf('.') !== -1) {
          e.preventDefault();
        }
      }
    }
    // only allow 2 decimals beyond period
    if (e.which >= 48 && e.which <= 90) {
      if (legalChars.indexOf(e.key) === -1) {
        e.preventDefault();
      }
    }
  };
  const handleFocus = (e) => {
    if (e && e.currentTarget) {
      e.currentTarget.select();
    }
    setHasFocus(true);
  };
  const handleSubmit = (e) => {
    if (onSubmit) {
      const ecopy = e;
      ecopy.target.value = validateField(fieldValue);
      onSubmit(ecopy);
      setHasFocus(false);
    }
  };
  const handleChange = (e) => {
    if (e && e.target) {
      // This if statement ensures that the suggested input does not violate the rules
      // of the field (e.g. more than 2 decimal places).  Do allow a single decimal
      if (e.target.value === '-' || e.target.value === '.' || Number(e.target.value) === Number(validateField(e.target.value))) {
        setFieldValue(e.target.value);
        // if onChange prop passed, copy event and call f'n
        if (onChange) {
          const ecopy = e;
          onChange(ecopy)
        }
      }
    }
  };
  const handleBlur = (e) => {
    if (onBlur) {
      const ecopy = e;
      e.target.value = validateField(fieldValue);
      onBlur(ecopy);
    }
    setHasFocus(false);
  };
  const validateField = (val) => Number(val.replace(',', '')).toFixed(omitCents ? 0 : 2);

  // RENDER ============================================================================

  return (
    <div id={id} className={classes.root} style={{...wrapperStyles}}>
      {!editable &&
        <Typography
          style={{...styles, ...styleProps}}
          variant='body1'
        >
          {fieldValue}
        </Typography>
      }
      {editable &&
        <TextField
          {...textFieldProps}
          onChange={handleChange}
          value={fieldValue}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={submitOnBlur ? handleSubmit : handleBlur}
          autoFocus={autoFocus}
          InputLabelProps={{
            classes: {
              root: classes.noWrap,
            },
          }}
          InputProps={{
            ...InputProps,
            inputProps: {
              className: classNames(classes.inputField),
              maxLength: 20,
            },
            startAdornment: (
              <InputAdornment classes={{ root: classes.adornment}} disableTypography position="end">
                {currencySymbol}
              </InputAdornment>
            ),
          }}
        />
      }
    </div>
  )
};

QAmountField.propTypes = propTypes;

export default (QAmountField);


